// =============================================================================
// SHANIZ GAMING ADMIN - SUPPORT TICKET DETAIL
// =============================================================================

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as BackIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatListBulleted as ListIcon,
  Code as CodeIcon,
  ArrowUpward as EscalateIcon,
  Lock as CloseIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { supportService, SupportTicket, TicketMessage } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';
import { useAdminSocket } from '../hooks/useAdminSocket';

// Backend may return either the rich `author:{id,name,type}` object we ship in optimistic
// updates, or the flat `authorRole` + `authorId` it stores in the DB. Normalise both.
function msgAuthor(msg: any): { name: string; type: 'user' | 'admin' | 'system' } {
  if (msg?.author && typeof msg.author === 'object') {
    return {
      name: String(msg.author.name ?? msg.author.username ?? msg.authorId ?? '—'),
      type: (msg.author.type as 'user' | 'admin' | 'system') ?? 'user',
    };
  }
  const role = String(msg?.authorRole ?? '').toUpperCase();
  const type: 'user' | 'admin' | 'system' =
    role === 'ADMIN' || role === 'SUPER_ADMIN' || role === 'MODERATOR' || role === 'SUPPORT'
      ? 'admin'
      : role === 'SYSTEM'
      ? 'system'
      : 'user';
  return { name: role || 'User', type };
}

const Bubble: React.FC<{ msg: TicketMessage; self: boolean }> = ({ msg, self }) => {
  const author = msgAuthor(msg);
  return (
    <Box sx={{ display: 'flex', justifyContent: self ? 'flex-end' : 'flex-start', mb: 2 }}>
      {!self && (
        <Avatar sx={{ mr: 1, bgcolor: 'primary.main' }}>
          {author.name.charAt(0) || '?'}
        </Avatar>
      )}
      <Box
        sx={{
          maxWidth: '70%',
          bgcolor: self ? 'primary.main' : '#1A1A1A',
          color: self ? 'primary.contrastText' : 'text.primary',
          px: 2,
          py: 1.25,
          borderRadius: 2,
          border: self ? 'none' : '1px solid #262626',
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {author.name} · {msg.createdAt ? dayjs(msg.createdAt).format('MMM D HH:mm') : ''}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(msg.body ?? '')) }} />
        {Array.isArray(msg.attachments) && msg.attachments.map((a) => (
          <Typography key={a.url} variant="caption" sx={{ display: 'block', textDecoration: 'underline' }}>
            <a href={a.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{a.name}</a>
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

// Minimal sanitizer — whitelist simple formatting tags produced by our toolbar.
function sanitizeHtml(input: string): string {
  const allowed = /^(b|strong|i|em|u|ul|ol|li|br|p|code|pre)$/i;
  return input.replace(/<\/?([a-zA-Z0-9]+)[^>]*>/g, (m, tag) => (allowed.test(tag) ? m.replace(/ [^>]*/, '') : ''));
}

const SupportTicketPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const feedEnd = useRef<HTMLDivElement | null>(null);
  const [replyHtml, setReplyHtml] = useState('');
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateNote, setEscalateNote] = useState('');
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeResolution, setCloseResolution] = useState('');
  const [assignId, setAssignId] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });

  const ticket = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => supportService.getById(id!),
    enabled: Boolean(id),
  });

  const admins = useQuery({
    queryKey: ['admins'],
    queryFn: supportService.listAdmins,
  });

  useEffect(() => {
    if (ticket.data?.assignedToId) setAssignId(ticket.data.assignedToId);
  }, [ticket.data?.assignedToId]);

  useEffect(() => {
    feedEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket.data?.messages?.length]);

  useAdminSocket((ev) => {
    if (ev.type === 'ticket.message' && ev.payload.ticketId === id) {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
    }
  });

  const reply = useMutation({
    mutationFn: (body: string) => supportService.reply(id!, body),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['ticket', id] });
      const prev = qc.getQueryData<SupportTicket>(['ticket', id]);
      if (prev) {
        const optimistic: TicketMessage = {
          id: `temp-${Date.now()}`,
          ticketId: id!,
          author: { id: 'me', name: 'You', type: 'admin' },
          body,
          createdAt: new Date().toISOString(),
        };
        qc.setQueryData<SupportTicket>(['ticket', id], {
          ...prev,
          messages: [...(prev.messages || []), optimistic],
        });
      }
      return { prev };
    },
    onError: (e: Error, _b, ctx) => {
      if (ctx?.prev) qc.setQueryData(['ticket', id], ctx.prev);
      setSnackbar({ open: true, msg: e.message || 'Reply failed', sev: 'error' });
    },
    onSuccess: () => {
      setReplyHtml('');
      if (editorRef.current) editorRef.current.innerHTML = '';
      qc.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });

  const escalate = useMutation({
    mutationFn: (note: string) => supportService.escalate(id!, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      setEscalateOpen(false);
      setEscalateNote('');
      setSnackbar({ open: true, msg: 'Ticket escalated', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Escalate failed', sev: 'error' }),
  });

  const closeM = useMutation({
    mutationFn: (resolution: string) => supportService.close(id!, resolution),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      setCloseOpen(false);
      setCloseResolution('');
      setSnackbar({ open: true, msg: 'Ticket closed', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Close failed', sev: 'error' }),
  });

  const assign = useMutation({
    mutationFn: (adminId: string) => supportService.assign(id!, adminId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      setSnackbar({ open: true, msg: 'Ticket reassigned', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Assign failed', sev: 'error' }),
  });

  if (!id) return <Alert severity="error">Ticket id missing in URL.</Alert>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <IconButton onClick={() => nav('/support')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Ticket #{id.slice(0, 8)}
        </Typography>
        {ticket.data && <Chip label={String(ticket.data.status ?? '').replace('_', ' ')} size="small" />}
      </Stack>

      {ticket.isLoading && <Skeleton variant="rectangular" height={420} />}
      {ticket.isError && <Alert severity="error">Failed to load ticket.</Alert>}
      {ticket.data && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>{ticket.data.subject}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {ticket.data.category} · {ticket.data.priority} · opened {dayjs(ticket.data.createdAt).format('MMM D, YYYY')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ maxHeight: 500, overflowY: 'auto', pr: 1 }}>
                  {(Array.isArray(ticket.data.messages) ? ticket.data.messages : []).map((m) => (
                    <Bubble key={m.id} msg={m} self={msgAuthor(m).type === 'admin'} />
                  ))}
                  <div ref={feedEnd} />
                </Box>
                <Divider sx={{ my: 2 }} />

                {/* Rich text composer */}
                <Toolbar variant="dense" sx={{ px: 0, gap: 0.5 }}>
                  <IconButton size="small" onClick={() => document.execCommand('bold')}>
                    <BoldIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => document.execCommand('italic')}>
                    <ItalicIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => document.execCommand('insertUnorderedList')}>
                    <ListIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => {
                    const sel = window.getSelection()?.toString() || '';
                    document.execCommand('insertHTML', false, `<code>${sel || 'code'}</code>`);
                  }}>
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Toolbar>
                <Box
                  ref={editorRef}
                  contentEditable
                  onInput={(e) => setReplyHtml((e.target as HTMLDivElement).innerHTML)}
                  sx={{
                    minHeight: 120,
                    p: 1.5,
                    bgcolor: '#141414',
                    borderRadius: 1,
                    border: '1px solid #262626',
                    '&:focus': { outline: 'none', borderColor: 'primary.main' },
                  }}
                  suppressContentEditableWarning
                />
                <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
                  <PermissionButton
                    permission="tickets.reply"
                    variant="contained"
                    endIcon={<SendIcon />}
                    disabled={!replyHtml.trim() || reply.isPending}
                    onClick={() => reply.mutate(replyHtml)}
                  >
                    Send reply
                  </PermissionButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>User</Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar src={ticket.data.user?.profilePicture || undefined}>
                    {ticket.data.user?.username?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{ticket.data.user?.username}</Typography>
                    <Typography variant="caption" color="text.secondary">{ticket.data.user?.email}</Typography>
                  </Box>
                </Stack>
                <Divider sx={{ my: 2 }} />
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Assign to</InputLabel>
                  <Select
                    label="Assign to"
                    value={assignId}
                    onChange={(e) => {
                      const v = e.target.value as string;
                      setAssignId(v);
                      if (v) assign.mutate(v);
                    }}
                  >
                    <MenuItem value=""><em>Unassigned</em></MenuItem>
                    {(Array.isArray(admins.data) ? admins.data : []).map((a) => (
                      <MenuItem key={a.id} value={a.id}>{a.username} ({a.role})</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack spacing={1}>
                  <PermissionButton
                    permission="tickets.escalate"
                    variant="outlined"
                    color="warning"
                    startIcon={<EscalateIcon />}
                    onClick={() => setEscalateOpen(true)}
                    disabled={ticket.data.status === 'ESCALATED' || ticket.data.status === 'CLOSED'}
                  >
                    Escalate
                  </PermissionButton>
                  <PermissionButton
                    permission="tickets.close"
                    variant="outlined"
                    color="success"
                    startIcon={<CloseIcon />}
                    onClick={() => setCloseOpen(true)}
                    disabled={ticket.data.status === 'CLOSED'}
                  >
                    Close ticket
                  </PermissionButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog open={escalateOpen} onClose={() => setEscalateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Escalate ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Escalation note"
            value={escalateNote}
            onChange={(e) => setEscalateNote(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEscalateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!escalateNote.trim() || escalate.isPending}
            onClick={() => escalate.mutate(escalateNote.trim())}
          >
            Escalate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close ticket</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Resolution"
            value={closeResolution}
            onChange={(e) => setCloseResolution(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!closeResolution.trim() || closeM.isPending}
            onClick={() => closeM.mutate(closeResolution.trim())}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportTicketPage;
